import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import PhoneNumberField from '../components/PhoneNumberField';
import emailjs from '@emailjs/browser';
import { formatPhoneWithCountryCode, getIndianPhoneError } from '../utils/phoneUtils';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [country, setCountry] = useState('in');
    const [countryCode, setCountryCode] = useState('+91');
    const [phoneError, setPhoneError] = useState('');
    const [alertMessage, setAlertMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (event) => {
        const { id, value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handlePhoneChange = (phoneValue, countryInfo = {}) => {
        setFormData(prevState => ({ ...prevState, phone: phoneValue }));
        
        const newCountryIso = countryInfo.countryIso || country;
        const newCountryCode = countryInfo.countryCode || countryCode;
        
        if (countryInfo.countryIso) setCountry(newCountryIso);
        if (countryInfo.countryCode) setCountryCode(newCountryCode);
        
        setPhoneError(getIndianPhoneError(phoneValue, newCountryCode));
    };

    const sendEmail = (event) => {
        event.preventDefault();

        const trimmedData = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim()
        };

        const phoneValidation = getIndianPhoneError(trimmedData.phone, countryCode);

        if (!trimmedData.name || !trimmedData.email || !trimmedData.phone || !trimmedData.subject || !trimmedData.message) {
            setPhoneError(phoneValidation);
            setAlertMessage({ text: 'Please fill in all fields.', type: 'error' });
            return;
        }

        if (phoneValidation) {
            setPhoneError(phoneValidation);
            setAlertMessage({ text: phoneValidation, type: 'error' });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedData.email)) {
            setAlertMessage({ text: 'Please enter a valid email address.', type: 'error' });
            return;
        }

        setIsLoading(true);

        const submissionData = {
            ...trimmedData,
            phone: formatPhoneWithCountryCode(countryCode, trimmedData.phone)
        };

        emailjs.send('service_v6l9rbv', 'template_zja3scs', submissionData)
            .then((response) => {
                console.log('SUCCESS!', response.status, response.text);
                setAlertMessage({ text: 'Your message has been sent successfully!', type: 'success' });
                setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                setPhoneError('');
                setIsLoading(false);
            })
            .catch((error) => {
                console.log('FAILED...', error);
                setAlertMessage({ text: 'Oops! Something went wrong. Please try again later.', type: 'error' });
                setIsLoading(false);
            });
    };

    useEffect(() => {
        emailjs.init("GPGBbocMM4O1llkd1");
        // Force hide spinner immediately
        const spinner = document.getElementById('spinner');
        if (spinner) {
            spinner.classList.remove('show');
            spinner.style.display = 'none';
            spinner.style.opacity = '0';
            spinner.style.visibility = 'hidden';
        }
        if (window.WOW) {
            new window.WOW().init();
        }
    }, []);

    return (
        <>
            <WhatsAppFloat />
            <Navbar />

            {/* Page Header Start */}
            <div className="container-fluid page-header mb-5 wow fadeIn" data-wow-delay="0.1s">
                <div className="container">
                    <h1 className="display-3 mb-4 animated slideInDown">Contact</h1>
                    <nav aria-label="breadcrumb animated slideInDown">
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item"><a href="/">Home</a></li>
                            <li className="breadcrumb-item"><a href="/contact">Pages</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Contact</li>
                        </ol>
                    </nav>
                </div>
            </div>
            {/* Page Header End */}

            {/* Contact Start */}
            <div className="container-xxl py-5">
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-6 wow fadeIn" data-wow-delay="0.1s">
                            <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">Contact</p>
                            <h1 className="display-5 mb-4">Ready to Roar Online?<br /> Get in Touch with WebBears</h1>
                            {alertMessage.text && (
                                <div style={{
                                    backgroundColor: alertMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                                    color: alertMessage.type === 'success' ? '#155724' : '#721c24',
                                    border: alertMessage.type === 'success' ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
                                    padding: '10px 15px',
                                    borderRadius: '4px',
                                    marginBottom: '20px'
                                }}>
                                    {alertMessage.text}
                                </div>
                            )}
                            <form id="contact"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    sendEmail(e);
                                }}>
                                <div className="row g-3">
                                    <div className="col-sm-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                placeholder="Your Name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                            />
                                            <label htmlFor="name">Your Name</label>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                placeholder="Your Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                            <label htmlFor="email">Your Email</label>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <PhoneNumberField
                                            phone={formData.phone}
                                            country={country}
                                            onPhoneChange={handlePhoneChange}
                                            error={phoneError}
                                        />
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="subject"
                                                placeholder="Subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                            />
                                            <label htmlFor="subject">Subject</label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <textarea
                                                className="form-control"
                                                placeholder="Leave a message here"
                                                id="message"
                                                style={{ height: '100px' }}
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                            ></textarea>
                                            <label htmlFor="message">Message</label>
                                        </div>
                                    </div>
                                    <div className="col-12 text-center">
                                        <button className="btn btn-primary w-100 py-3" type="submit" id="form-submit" disabled={isLoading}>
                                            {isLoading ? 'Sending...' : 'Submit Now'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {/* Contact End */}

            <Footer />
        </>
    );
};

export default Contact;
